"use client";

import { useEffect, useLayoutEffect, useRef } from "react";

const stateKey = "portalDocsScroll";
const positions = new Map<string, { top: number; left: number }>();
let restoring = false;

export function isRestoringDocsScroll() {
  return restoring;
}

export function useDocsScrollRestoration(pathname: string) {
  const committedPath = useRef(pathname);
  const entry = useRef<string | null>(null);
  const pending = useRef<string | null>(null);
  const frame = useRef(0);
  const restore = useRef(() => {});

  useLayoutEffect(() => {
    const container = document.getElementById("docs-scroll-container");
    if (!container) return;
    const save = () => {
      if (
        entry.current &&
        !restoring &&
        committedPath.current === location.pathname
      ) {
        positions.set(entry.current, {
          top: container.scrollTop,
          left: container.scrollLeft,
        });
      }
    };
    const identify = () => {
      const key = crypto.randomUUID();
      history.replaceState({ ...history.state, [stateKey]: key }, "");
      entry.current = key;
    };
    const existing = history.state?.[stateKey];
    if (existing && positions.has(existing)) {
      entry.current = existing;
      pending.current = existing;
      restoring = true;
    } else identify();

    let deadline: ReturnType<typeof setTimeout> | undefined;
    const finish = () => {
      clearTimeout(deadline);
      pending.current = null;
      restoring = false;
      save();
    };
    const applyPosition = () => {
      const position = pending.current
        ? positions.get(pending.current)
        : undefined;
      if (position) {
        container.scrollTo({ ...position, behavior: "instant" });
        // Streamed content may not yet be tall enough. Keep the original target
        // until it fits instead of replacing it with the browser's clamped value.
        if (Math.abs(container.scrollTop - position.top) > 1) return;
      } else if (!location.hash) {
        container.scrollTo({ top: 0, left: 0, behavior: "instant" });
      }
      finish();
    };
    const resize = new ResizeObserver(() => {
      if (pending.current) {
        cancelAnimationFrame(frame.current);
        frame.current = requestAnimationFrame(applyPosition);
      }
    });
    if (container.firstElementChild)
      resize.observe(container.firstElementChild);
    const stopForUser = () => {
      if (!pending.current) return;
      cancelAnimationFrame(frame.current);
      finish();
    };
    const onKey = (event: KeyboardEvent) => {
      if (
        [
          "ArrowUp",
          "ArrowDown",
          "PageUp",
          "PageDown",
          "Home",
          "End",
          " ",
        ].includes(event.key)
      )
        stopForUser();
    };
    container.addEventListener("wheel", stopForUser, { passive: true });
    container.addEventListener("touchstart", stopForUser, { passive: true });
    container.addEventListener("keydown", onKey);
    restore.current = () => {
      clearTimeout(deadline);
      if (pending.current) deadline = setTimeout(finish, 5000);
      cancelAnimationFrame(frame.current);
      // Let Next's focus/scroll handling and article anchor effects finish first.
      frame.current = requestAnimationFrame(() => {
        frame.current = requestAnimationFrame(() => {
          applyPosition();
        });
      });
    };
    const onPop = (event: PopStateEvent) => {
      // The outgoing entry's position is already tracked by its scroll events.
      const key = event.state?.[stateKey];
      if (key && key !== entry.current && positions.has(key)) {
        entry.current = key;
        pending.current = key;
        restoring = true;
      } else {
        identify();
        pending.current = null;
        restoring = false;
      }
      if (committedPath.current === location.pathname) restore.current();
    };
    container.addEventListener("scroll", save, { passive: true });
    window.addEventListener("popstate", onPop);
    window.addEventListener("pagehide", save);
    return () => {
      save();
      clearTimeout(deadline);
      resize.disconnect();
      container.removeEventListener("wheel", stopForUser);
      container.removeEventListener("touchstart", stopForUser);
      container.removeEventListener("keydown", onKey);
      cancelAnimationFrame(frame.current);
      container.removeEventListener("scroll", save);
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("pagehide", save);
      restoring = false;
    };
  }, []);

  useEffect(() => {
    if (committedPath.current !== pathname) {
      committedPath.current = pathname;
      if (!pending.current) {
        const key = crypto.randomUUID();
        history.replaceState({ ...history.state, [stateKey]: key }, "");
        entry.current = key;
      }
    }
    restore.current();
  }, [pathname]);
}
